import React, { useState } from 'react';
import './SellersEngagementDocuments.scss';
import SellersEngagementDocumentsSection from './SellersEngagementDocumentsSection';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { EngagementData } from '../../../../slices/marketplace/timeline/models/engagement-data.model';
import { Button, Label } from 'reactstrap';
import { useStateSelector } from '../../../../store/selectors';
import { USER_ROLES } from '../../../../utils/constants';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import { ActionDocumentType } from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import createNotification from '../../../../utils/createNotification';
import { EngagementDocument } from '../../../../slices/marketplace/timeline/models/engagement-document';

interface SellersEngagementDocumentsProps {
    timelineId: number;
    engagementData: EngagementData;
    preSelectedDocumentId?: string;
    resetPreSelectedDocumentId?(): void;
}

const SellersEngagementDocuments = (props: SellersEngagementDocumentsProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const documentSections: DocumentSection[] = [
        ...props.engagementData.documentSectionsList,
    ].sort((a, b) => a.index - b.index);
    const [selectedDocumentsIds, setSelectedDocumentsIds] = useState<string[]>(
        []
    );
    const timeline = useStateSelector((t) => t.timelines.currentTimeline);
    const [isSelectModeEnabled, setIsSelectModeEnabled] =
        useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const isAdmin = useStateSelector(
        (state) => state.userSummary.summary.roleId === USER_ROLES.ADMIN
    );
    const [isGettingForSign, setIsGettingForSign] = useState<boolean>(false);
    const [downloadableDocumentsIds, setDownloadableDocumentsIds] = useState<
        string[][]
    >([]);

    const downloadableDocDetailsFilterCondition = (
        documentDetails: DocumentDetails | EngagementDocument
    ): boolean => {
        if (
            documentDetails.type === ActionDocumentType.ForSign &&
            documentDetails.status !== DocumentDetailsStatus.Completed
        ) {
            return false;
        }

        return (
            documentDetails.type !== ActionDocumentType.Readonly &&
            documentDetails.isPrepared
        );
    };

    const selectSection = () => {
        const areAllAvailableDocumentsSelected = () =>
            downloadableDocumentsIds.reduce(
                (total, row) => total + (row?.length ?? 0),
                0
            ) === selectedDocumentsIds.length;

        const fetchDocuments = async () => {
            const queryParams = selectedDocumentsIds
                .map((url) => `documentDetailsIds=${encodeURIComponent(url)}`)
                .join('&');
            const response = await axios.get(
                `/api/marketplace/engagements/${timeline.id}/download-documents?${queryParams}`,
                {
                    responseType: 'blob',
                }
            );
            return response;
        };

        const downloadFiles = async () => {
            setIsDownloading(true);
            const response = await fetchDocuments();
            if (response.status !== 200) {
                createNotification(
                    'An unexpected error occurred while downloading documents',
                    'error'
                );
                setIsDownloading(false);
                return;
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const fileName =
                selectedDocumentsIds.length == 1
                    ? timeline.engagementData.documentsList.find(
                          (doc) => doc.id == selectedDocumentsIds[0]
                      )?.fileName
                    : 'EngagementDocuments.zip';
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setSelectedDocumentsIds([]);
            setIsSelectModeEnabled(false);
            setIsDownloading(false);
        };

        return isSelectModeEnabled ? (
            <div className="select-documents-btns-panel">
                <Label className="selected-documents-count">
                    Selected: {selectedDocumentsIds.length}
                </Label>
                <Button
                    className="btn-cancel"
                    onClick={() => {
                        setIsSelectModeEnabled(false);
                        setSelectedDocumentsIds([]);
                    }}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={() => {
                        if (areAllAvailableDocumentsSelected()) {
                            setSelectedDocumentsIds([]);
                            return;
                        }
                        setSelectedDocumentsIds(
                            downloadableDocumentsIds.reduce((total, row) =>
                                total.concat(row)
                            )
                        );
                    }}>
                    {areAllAvailableDocumentsSelected()
                        ? 'Unselect All'
                        : 'Select All'}
                </Button>

                <ButtonLoader
                    disabled={selectedDocumentsIds.length === 0}
                    onClick={() => downloadFiles()}
                    buttonText="Download Selected"
                    loaderButtonText={'Download Selected'}
                    isLoading={isDownloading}
                    className={
                        isDownloading
                            ? 'btn btn-primary loader-submit'
                            : 'btn btn-primary'
                    }></ButtonLoader>
            </div>
        ) : (
            <Button
                color="primary"
                className="select-documents-btn"
                onClick={() => setIsSelectModeEnabled(true)}>
                Select For Download
            </Button>
        );
    };

    const onDocumentSelected = (documentId: string) => {
        if (isSelectModeEnabled) {
            setSelectedDocumentsIds((prevSelectedDocumentsIds) => {
                if (prevSelectedDocumentsIds.includes(documentId)) {
                    return prevSelectedDocumentsIds.filter(
                        (id) => id !== documentId
                    );
                } else {
                    return [...prevSelectedDocumentsIds, documentId];
                }
            });
        }
    };

    return (
        <div className="sellers-engagement-documents">
            {documentSections.map((section, index) => {
                return (
                    <SellersEngagementDocumentsSection
                        key={section.id}
                        buttonsSection={
                            index === 0 ? selectSection() : undefined
                        }
                        isSelectModeEnabled={isSelectModeEnabled}
                        selectedDocumentsIds={selectedDocumentsIds}
                        timelineId={props.timelineId}
                        engagementDocumenstSection={section}
                        onDocumentSelected={onDocumentSelected}
                        isAdminView={isAdmin}
                        isGettingForSign={isGettingForSign}
                        setIsGettingForSign={setIsGettingForSign}
                        downloadableDocDetailsFilterCondition={
                            downloadableDocDetailsFilterCondition
                        }
                        preSelectedDocumentId={props.preSelectedDocumentId}
                        resetPreSelectedDocumentId={
                            props.resetPreSelectedDocumentId
                        }
                        setDownloadableDocumentsIds={(ids: string[]) => {
                            setDownloadableDocumentsIds((prevDocs) => {
                                const docs = [...prevDocs];
                                docs[index] = ids;
                                return docs;
                            });
                        }}
                    />
                );
            })}
        </div>
    );
};

export default SellersEngagementDocuments;
